import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiHeart, FiShoppingCart, FiCheckCircle, FiX } from 'react-icons/fi';
import { useFavorites } from '../hooks/useFavorites';
import { useCart } from '../hooks/useCart';
import { useLanguage } from '../context/LanguageContext';
import { useAuthContext } from '../hooks/useAuthContext';
import LoginModal from './LoginModal';
import { slugify } from '../utils/slugify';
import { supabase } from '../config/supabase';

const Card = styled.div`
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s;
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }
`;

const ImageWrapper = styled(Link)`
  position: relative;
  width: 100%;
  max-height: 260px;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;

  @media (max-width: 640px) {
    padding: 8px;
    max-height: 220px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: #ef4444;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  z-index: 2;
  
  @media (max-width: 640px) {
    top: 8px;
    left: 8px;
    padding: 3px 6px;
    font-size: 11px;
    border-radius: 3px;
  }
`;

const ActionButtons = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s;

  ${Card}:hover & {
    opacity: 1;
  }

  /* On touch devices there is no real hover; make buttons always tappable. */
  @media (hover: none) and (pointer: coarse) {
    opacity: 1;
  }
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
    color: #0f172a;
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(94, 251, 169, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  
  @media (max-width: 640px) {
    padding: 12px;
    gap: 5px;
  }
`;

const SellerLink = styled(Link)`
  font-size: 12px;
  color: #6b7280;
  text-decoration: none;
  margin-top: 4px;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 16px;
  line-height: 16px;

  &:hover {
    color: #5efba9;
  }
`;

const Title = styled(Link)`
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  text-decoration: none;
  display: block;
  margin-bottom: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: calc(1.35em * 2);

  &:hover {
    color: #5efba9;
  }
  
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  min-height: 30px;
  
  @media (max-width: 640px) {
    gap: 8px;
    margin-bottom: 10px;
    min-height: 28px;
  }
`;

const CurrentPrice = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #5efba9;
  
  @media (max-width: 640px) {
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  color: #9ca3af;
  text-decoration: line-through;
  
  @media (max-width: 640px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const AddToCartButton = styled.button`
  width: 100%;
  margin-top: auto;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
  font-size: 14px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.4);
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 640px) {
    padding: 10px;
    font-size: 13px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 9px;
    font-size: 12px;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Toast = styled.div`
  background: ${props => props.type === 'success' ? '#f0fdf4' : '#fef2f2'};
  border: 1px solid ${props => props.type === 'success' ? '#bbf7d0' : '#fecaca'};
  border-left: 4px solid ${props => props.type === 'success' ? '#22c55e' : '#ef4444'};
  color: ${props => props.type === 'success' ? '#15803d' : '#dc2626'};
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const ToastIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
`;

const ToastMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

function ProductCard({ product }) {
  const { id, title, name, name_ar, price, originalPrice, discount, image, seller, currency, brand } = product;
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const { addToCart } = useCart();
  const { isArabic } = useLanguage();
  const { session } = useAuthContext();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [hasVariants, setHasVariants] = useState(null); // null = not checked yet, true/false = checked
  const favorite = isFavorite(id);

  // Display name based on language
  const displayName = isArabic ? (name_ar || name || title) : (name || title);

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    const newToast = { id, message, type };
    setToasts(prev => [...prev, newToast]);

    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isProcessing) return;

    try {
      setIsProcessing(true);
      if (favorite) {
        const res = await removeFavorite(id);
        // if hook returns { error } style response, throw it so catch handles it
        if (res && res.error) throw res.error;
        showToast('Removed from wishlist', 'success');
      } else {
        const res = await addFavorite(id);
        // if hook returns { error } style response, throw it so catch handles it
        if (res && res.error) throw res.error;
        showToast('Added to wishlist', 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Extract meaningful message if available
      const message =
        (error && (error.message || error.error || error.msg)) ||
        (typeof error === 'string' ? error : null) ||
        JSON.stringify(error) ||
        'Failed to update wishlist';
      showToast(message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if product has variants/options
  const checkForVariants = async () => {
    if (hasVariants !== null) return hasVariants; // Already checked
    
    try {
      const { data, error } = await supabase
        .from('product_option_groups')
        .select('id')
        .eq('product_id', id)
        .limit(1);
      
      const hasOptions = !error && data && data.length > 0;
      setHasVariants(hasOptions);
      return hasOptions;
    } catch (error) {
      console.error('Error checking for variants:', error);
      setHasVariants(false);
      return false;
    }
  };

  // Actual add to cart logic (without session check)
  const performAddToCart = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      console.log('Adding product to cart:', id);
      await addToCart(id, 1);
      console.log('Successfully added to cart');
      showToast('Item added to cart successfully!', 'success');
    } catch (error) {
      console.error('Error adding to cart:', error);
      console.error('Error details:', error.message, error);
      // Only show error if it's not a login error (shouldn't happen now, but just in case)
      if (error.message && error.message.includes('must be logged in')) {
        setShowLoginModal(true);
      } else {
        showToast(`Failed to add item to cart: ${error.message || 'Unknown error'}`, 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if user is logged in
    if (!session) {
      setShowLoginModal(true);
      return;
    }

    // Check if product has variants
    const hasOptions = await checkForVariants();
    
    if (hasOptions) {
      // Navigate to product page to select options
      navigate(`/product/${id}`);
      return;
    }

    // No variants, add directly to cart
    await performAddToCart();
  };

  return (
    <>
      <Card>
        <ImageWrapper to={`/product/${id}`}>
          <Image src={image || 'https://via.placeholder.com/300x240'} alt={displayName} />
          {discount && <Badge>-{discount}%</Badge>}
        </ImageWrapper>

        <ActionButtons>
          <IconButton
            aria-label={favorite ? "Remove from wishlist" : "Add to wishlist"}
            onClick={handleToggleFavorite}
            style={{ color: favorite ? '#ef4444' : 'inherit' }}
          >
            <FiHeart />
          </IconButton>
          <IconButton
            aria-label={hasVariants === true ? "See options" : "Quick add to cart"}
            onClick={handleAddToCart}
            disabled={isProcessing}
          >
            <FiShoppingCart />
          </IconButton>
        </ActionButtons>

        <Content>
          <InfoSection>
            <Title to={`/product/${id}`}>{displayName}</Title>
            {(brand || seller) && (
              <SellerLink to={`/${slugify(brand || seller?.name || 'brand')}`}>
                {brand || seller?.name || 'Brand'}
              </SellerLink>
            )}
            <PriceContainer>
              <CurrentPrice>{currency || 'OMR'} {price}</CurrentPrice>
              {originalPrice && <OriginalPrice>{originalPrice} OMR</OriginalPrice>}
            </PriceContainer>
          </InfoSection>
          <AddToCartButton onClick={handleAddToCart} disabled={isProcessing}>
            <FiShoppingCart /> {
              isProcessing 
                ? (isArabic ? 'جاري الإضافة...' : 'Adding...') 
                : hasVariants === true
                  ? (isArabic ? 'راجع الخيارات' : 'See Options')
                  : (isArabic ? 'أضف إلى السلة' : 'Add to Cart')
            }
          </AddToCartButton>
        </Content>
      </Card>

      {toasts.length > 0 && (
        <ToastContainer>
          {toasts.map(toast => (
            <Toast key={toast.id} type={toast.type}>
              <ToastIcon>
                {toast.type === 'success' ? <FiCheckCircle /> : <FiX />}
              </ToastIcon>
              <ToastMessage>{toast.message}</ToastMessage>
              <ToastClose onClick={() => removeToast(toast.id)}>
                <FiX />
              </ToastClose>
            </Toast>
          ))}
        </ToastContainer>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
        />
      )}
    </>
  );
}

export default ProductCard;
